/**
 * Storage utilities for managing localStorage data consistently
 */

/**
 * Check if user has valid login credentials in localStorage
 * @returns {boolean} - true if valid login data exists and remember me was enabled
 */
export const hasValidLoginCredentials = () => {
  try {
    const loginData = localStorage.getItem("loginSuccess");
    const userCredentials = localStorage.getItem("userCredentials");

    // Only consider valid if both login data exists AND remember me was enabled (userCredentials exists)
    return (
      loginData !== null &&
      loginData !== "undefined" &&
      userCredentials !== null
    );
  } catch (error) {
    console.error("Error checking login credentials:", error);
    return false;
  }
};

/**
 * Get login data from localStorage
 * @returns {object|null} - parsed login data or null (only if remember me was enabled)
 */
export const getStoredLoginData = () => {
  try {
    const loginData = localStorage.getItem("loginSuccess");
    const userCredentials = localStorage.getItem("userCredentials");

    // Only return login data if remember me was enabled (userCredentials exists)
    if (loginData && userCredentials) {
      return JSON.parse(loginData);
    }
    return null;
  } catch (error) {
    console.error("Error parsing stored login data:", error);
    return null;
  }
};

/**
 * Get stored user credentials
 * @returns {object|null} - parsed credentials or null
 */
export const getStoredUserCredentials = () => {
  try {
    const credentials = localStorage.getItem("userCredentials");
    return credentials ? JSON.parse(credentials) : null;
  } catch (error) {
    console.error("Error parsing stored credentials:", error);
    return null;
  }
};

/**
 * Save login data to localStorage
 * @param {object} loginData - login response data
 */
export const saveLoginData = (loginData) => {
  try {
    localStorage.setItem("loginSuccess", JSON.stringify(loginData));
  } catch (error) {
    console.error("Error saving login data:", error);
  }
};

/**
 * Save user credentials to localStorage
 * @param {string} email
 * @param {string} password
 */
export const saveUserCredentials = (email, password) => {
  try {
    const credentials = { email, password };
    localStorage.setItem("userCredentials", JSON.stringify(credentials));
  } catch (error) {
    console.error("Error saving user credentials:", error);
  }
};

/**
 * Clear all authentication related data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem("loginSuccess");
    localStorage.removeItem("userCredentials");
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

/**
 * Clear only login data (when remember me is unchecked but user is still in session)
 */
export const clearLoginData = () => {
  try {
    localStorage.removeItem("loginSuccess");
  } catch (error) {
    console.error("Error clearing login data:", error);
  }
};

/**
 * Clear only user credentials (when remember me is unchecked)
 */
export const clearUserCredentials = () => {
  try {
    localStorage.removeItem("userCredentials");
  } catch (error) {
    console.error("Error clearing user credentials:", error);
  }
};

/**
 * Check if remember me was enabled (based on presence of userCredentials)
 * @returns {boolean}
 */
export const isRememberMeEnabled = () => {
  return getStoredUserCredentials() !== null;
};
